import toast from "react-hot-toast";
import { memo, ReactChild, useEffect, useMemo, useRef, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useSearchParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Paper,
  Popover,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";

import { DeviceKey } from "../../../types";
import Loader from "../../../Components/Loader/Loader";
import DataTable from "../../../Components/DataGrid/DataGrid";
import { customerApi } from "../../api/customer/customer.service";
import DeviceKeyManagementForm, { DeviceKeyManagementFormData } from "./DeviceKeysManagementForm";
import { deviceKeysApi } from "../../api/device-keys/device-keys.service";
import { deviceTypeApi } from "../../api/device-types/device-type.service";

type RangeItem = {
  cmin: number;
  cmax: number;
  wmin: number;
  wmax: number;
  unit: string;
};

type GroupedDeviceKeys = {
  alias: string;
  deviceType: string;
  key: string;
  id: string;
  customerId: string;
  ranges: Array<RangeItem>;
  customerTitle: string;
  deviceTypeLabel: string;
};
function getDeviceKeyId(key: DeviceKey) {
  return `${key.customerId}_${key.deviceType}_${key.key}`;
}

function getModalProps(modalMode: string | null): Partial<DialogProps> {
  if (modalMode === "delete") {
    return {
      maxWidth: "sm",
    };
  }

  return {
    maxWidth: "lg",
    fullWidth: true,
  };
}

function getModalTitle(modalMode: string | null) {
  if (modalMode === "create") {
    return "Add New Device Key";
  }

  if (modalMode === "edit") {
    return "Edit Device Key";
  }

  return "Delete Device Key";
}

const ActionsColumns = (params: GridRenderCellParams) => {
  const [_, setSearchParams] = useSearchParams();
  const id = getDeviceKeyId(params.row);

  function openEditModal() {
    setSearchParams(
      {
        modal: "edit",
        id: id,
      },
      { replace: true },
    );
  }

  function openDeleteModal() {
    setSearchParams(
      {
        modal: "delete",
        id: id,
      },
      { replace: true },
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openEditModal}>
        <EditIcon className="cursor-pointer" />
      </Tooltip>
      <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openDeleteModal}>
        <DeleteIcon className="cursor-pointer" />
      </Tooltip>
    </div>
  );
};

const OverflowTip: React.FC<{ children: ReactChild }> = ({ children }) => {
  return (
    <Tooltip
      title={
        <div className="overflow-tooltip-hover">
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Critical Min</TableCell>
                  <TableCell>Critical Max</TableCell>
                  <TableCell>Warning Min</TableCell>
                  <TableCell>Warning Max</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  {
                    cmax: 3.12,
                    cmin: 2.73,
                    wmin: 2.6,
                    wmax: 2.73,
                    unit: "v",
                  },
                  {
                    cmax: 1.71,
                    cmin: 1.44,
                    wmin: 1.71,
                    wmax: 1.8,
                    unit: "v",
                  },
                ].map((field, index) => (
                  <TableRow key={index}>
                    <TableCell>{field.cmin}</TableCell>
                    <TableCell>{field.cmax}</TableCell>
                    <TableCell>{field.wmin}</TableCell>
                    <TableCell>{field.wmax}</TableCell>
                    <TableCell>{field.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      }
      //   disableHoverListener={!isOverflowed}
      disableInteractive
    >
      <div>
        {/* {children} */}
        Range
      </div>
    </Tooltip>
  );
};

const RangeDisplayComponent = () => {
  const [open, setOpen] = useState(false);
  console.log("popper_state", open);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? "spring-popper" : undefined;

  return (
    <div>
      <button aria-describedby={id} type="button" onClick={handleClick}>
        Toggle Popper
      </button>
      <Popper id={id} open={open} anchorEl={anchorEl} transition>
        <div>The content of the Popper.</div>
      </Popper>
    </div>
  );
};

const columns: GridColDef[] = [
  {
    field: "actions",
    headerName: "",
    type: "actions",
    renderCell: ActionsColumns,
    hideable: false,
    disableExport: true,
  },
  {
    field: "customerTitle",
    headerName: "Customer",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "deviceType",
    headerName: "Device Type",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "deviceTypeLabel",
    headerName: "Device Type",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "key",
    headerName: "Key",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "alias",
    headerName: "Key Alias",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  //   {
  //     field: "ranges",
  //     headerName: "Range",
  //     sortable: true,
  //     flex: 2,
  //     width: 180,
  //     renderCell: (params) => {
  //       console.log("params.value", params.value);
  //       //   const ranges = params.value?.map((range: RangeItem, index: number) => (
  //       //     <Chip
  //       //       //   className="users__table__station-chip"
  //       //       key={"table_row" + index}
  //       //       //   label={`CMin: ${range.cmin}, CMax: ${range.cmax}, WMin: ${range.wmin}, WMax: ${range.wmax}, Unit: ${range.unit}`}
  //       //       label={`CMin: ${range.cmin}, CMax: ${range.cmax}`}
  //       //       size="small"
  //       //       variant="filled"
  //       //     />
  //       //   ));
  //       //   return <OverflowTip>{ranges}</OverflowTip>;
  //       return <RangeDisplayComponent />;
  //     },
  //   },
];

// const { usePostCustomerMutation, useDeleteCustomerMutation, useEditCustomerMutation } = customerApi;

const { useGetDeviceKeysQuery, useDeleteDeviceKeyMutation, useEditDeviceKeyMutation, usePostDeviceKeyMutation } =
  deviceKeysApi;

function getDeviceKeyListWithRange(device_keys: DeviceKey[]): GroupedDeviceKeys[] {
  const _map = new Map<string, GroupedDeviceKeys>();
  for (const key of device_keys) {
    const _id = getDeviceKeyId(key);
    const _item = _map.get(_id);

    const range_obj: RangeItem = {
      cmax: key.cmax,
      cmin: key.cmin,
      wmin: key.wmin,
      wmax: key.wmax,
      unit: key.unit,
    };

    if (_item) {
      const { ranges } = _item;
      ranges.push(range_obj);
    } else {
      _map.set(_id, {
        key: key.key,
        alias: key.alias,
        deviceType: key.deviceType,
        ranges: [range_obj],
        customerId: key.customerId,
        id: _id,
        customerTitle: key.customerTitle,
        deviceTypeLabel: key.deviceLabel,
      });
    }
  }

  const _list = Array.from(_map, ([id, val]) => val);
  console.log("list_is", _list);
  return _list;
}
const DeviceKeysManagement = () => {
  const { data: device_types = [] } = deviceTypeApi.useGetDeviceTypesQuery();
  const { data: customer_list = [] } = customerApi.useGetCustomersQuery();
  const { data = [], isFetching } = useGetDeviceKeysQuery();
  const [createRecord] = usePostDeviceKeyMutation();
  const [deleteRecord] = useDeleteDeviceKeyMutation();
  const [editRecord] = useEditDeviceKeyMutation();

  const parsed_device_keys = useMemo(() => getDeviceKeyListWithRange(data), [data]);

  console.log("data", data);

  const [searchParams, setSearchParams] = useSearchParams();

  // Determine modal state from search params
  const modalMode = searchParams.get("modal");
  const id = searchParams.get("id") ?? "";

  const initial_values = useMemo(() => {
    const DEFAULT_INITIAL_VALUE = {
      alias: "",
      deviceType: undefined,
      key: "",
      customerId: "00000000-0000-0000-0000-000000000000",
      ranges: [
        {
          cmin: "",
          cmax: "",
          wmin: "",
          wmax: "",
          unit: "",
        },
      ],
    };

    if (modalMode !== "edit" && !id) return DEFAULT_INITIAL_VALUE;
    const _device_key = parsed_device_keys.find((device_key) => device_key.id === id);
    // return DEFAULT_INITIAL_VALUE;
    // console.log("_customer", _customer);

    if (!_device_key) return DEFAULT_INITIAL_VALUE;

    return {
      ..._device_key,
      deviceType: device_types.find((device_type) => device_type.type === _device_key.deviceType),
      customer: customer_list.find((customer) => customer.customerId === _device_key.customerId),
    };
  }, [modalMode, id, device_types, parsed_device_keys]);

  // Open create modal
  const openCreateModal = () => {
    setSearchParams({ modal: "create" }, { replace: true });
  };

  const handleCloseModal = () => {
    setSearchParams({}, { replace: true }); // Removes all search params
  };

  async function handleFormSubmit(values: DeviceKeyManagementFormData) {
    console.log("values", values);

    const body = {
      ...values,
      deviceType: values.deviceType.type,
    };

    const toast_id = toast.loading("Submitting...");
    try {
      if (modalMode === "create") {
        await createRecord({ body }).unwrap();
      } else {
        await editRecord({
          params: { customerId: values.customerId, deviceType: values.deviceType.type, key: values.key },
          body,
        }).unwrap();
      }
      toast.success("Success", { id: toast_id });
      handleCloseModal();
    } catch (error) {
      console.error("SCHEDULED_REPORT_TEMPLATE_ERROR", error);
      toast.error("Unable to Submit Request", { id: toast_id });
    }
  }

  async function handleDeleteItem() {
    const toast_id = toast.loading("Deleting...");
    try {
      await deleteRecord({
        params: {
          deviceType: initial_values.deviceType?.type ?? "",
          key: initial_values.key,
          customerId: initial_values.customerId,
        },
      }).unwrap();
      toast.success("Success", { id: toast_id });
      handleCloseModal();
    } catch (error) {
      console.error("SCHEDULED_REPORT_TEMPLATE_DELETE_ERROR", error);
      toast.error("Unable to Submit Request", { id: toast_id });
    }
  }

  const FormComponent = memo(() => {
    if (modalMode === "edit" && isFetching)
      return (
        <div style={{ height: "10rem" }}>
          <Loader />
        </div>
      );

    if (modalMode === "delete") {
      return (
        <div>
          <div>Are you sure you want to delete this Device Key?</div>
          <div className="flex justify-end items-center gap-2 mt-2">
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={() => handleDeleteItem()}>
              Delete
            </Button>
          </div>
        </div>
      );
    }
    return (
      <DeviceKeyManagementForm
        defaultValues={initial_values}
        onSubmit={handleFormSubmit}
        editMode={modalMode === "edit"}
      />
    );
  });

  const modal_props = useMemo(() => getModalProps(modalMode), [modalMode]);
  const modal_title = getModalTitle(modalMode);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  return (
    <div style={{ height: "80vh"}}>
      {/* <div>
        <button aria-describedby={id} type="button" onClick={handleClick}>
          Toggle Popper
        </button>
        <Popover
          id={open ? "simple-popover" : undefined}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ border: 1, p: 1, bgcolor: "background.paper" }}>
            <div>The content of the Popper.</div>
          </Box>
        </Popover>
      </div> */}
      <Button variant="contained" onClick={openCreateModal} style={{ marginBottom: "0.5rem" }}>
        Create
      </Button>
      {(modalMode === "create" || modalMode === "edit" || modalMode === "delete") && (
        <Dialog open={true} onClose={handleCloseModal} {...modal_props}>
          <DialogTitle className="flex items-center justify-between">
            {modal_title}
            <CloseIcon className="assets-info-popup-close-icon pointer" onClick={handleCloseModal} />
          </DialogTitle>
          <DialogContent>
            <FormComponent />
          </DialogContent>
        </Dialog>
      )}
      <DataTable
        tableInfo={{
          id: "audit-log-table",
          label: "Audit Log",
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        loading={isFetching}
        rows={parsed_device_keys}
        columns={columns}
        showFilterButton={false}
        getRowId={getDeviceKeyId}
      />
    </div>
  );
};

export default DeviceKeysManagement;
